import { useEffect, useMemo } from 'react'
import useSWR, { SWRConfiguration } from 'swr'

import { ConsultationStatus, OrderStatus, UUID } from '../../@types'
import {
  findLastFeedbackAction,
  getMissedCalls,
  hasConsultationTerminatedStatus,
  isCallActionCancelled,
  isCallActionCompleted,
} from '../../lib/consultation'
import fetcher from '../../lib/fetcher'
import { revalidateOnRemount } from '../utils'

import { Order, PatientConsultation } from './types'

export const TWO_SECONDS = 2 * 1000

export function usePatientCurrentConsultation(uuid: UUID | null, configuration: SWRConfiguration<Order> = {}) {
  const key = uuid ? ['/order/' + uuid, { auth: true }] : null
  const config: SWRConfiguration<Order> = {
    onErrorRetry: (_err, _key, _config, revalidate) => setTimeout(revalidate, TWO_SECONDS),
    refreshInterval: TWO_SECONDS,
    ...revalidateOnRemount,
    ...configuration,
  }
  const { data: order, error, mutate } = useSWR<Order>(key, fetcher.api, config)

  useEffect(() => {
    if (error) {
      console.error('🔴 Error getting order', error)
    }
  }, [error])

  const patientConsultation: PatientConsultation | null = useMemo(() => {
    if (!order) return null

    // TODO: support for n consultations
    const consultation = order.consultations && order.consultations[order.consultations.length - 1]
    const lastFeedbackAction = consultation ? findLastFeedbackAction(consultation.actions) : null

    return {
      id: consultation?.id,
      encounterUuid: consultation?.encounterUuid,
      position: consultation?.position,
      preferredContactMethod: consultation?.preferredContactMethod,
      symptomCollectorCompleted: consultation?.symptomCollectorCompleted,
      symptomsDocuments: consultation?.symptomsDocuments,
      journalDocuments: consultation?.journalDocuments,
      order: {
        id: order.id,
        uuid: order.uuid,
        product: order.product,
        failureDomain: order.failureDomain,
        failureReason: order.failureReason,
        amount: order.amount,
        refundAmount: order.refundAmount,
        maskedCardNumber: order.maskedCardNumber,
        cardType: order.cardType,
        status: order.status,
        consultations: order.consultations,
        paymentMethodType: order.paymentMethodType,
        patient: order.patient,
        paymentId: order.paymentId,
      },
      orderStatus: {
        isProcessing: order.status === OrderStatus.PROCESSING,
        isFailed: order.status === OrderStatus.FAILED,
        isActive: order.status === OrderStatus.ACTIVE,
        isPending: order.status === OrderStatus.PENDING,
      },
      consultationStatus: {
        isQueued: consultation?.status === ConsultationStatus.QUEUED,
        isActive: consultation?.status === ConsultationStatus.ACTIVE,
        isClosed: consultation?.status === ConsultationStatus.CLOSED,
        isClosedWithError: consultation?.status === ConsultationStatus.CLOSED_WITH_ERROR,
        isExpired: consultation?.status === ConsultationStatus.EXPIRED,
        isCancelled:
          consultation?.status === ConsultationStatus.CANCELLED ||
          consultation?.status === ConsultationStatus.CANCELLING,
        hasClosedStatus: consultation ? hasConsultationTerminatedStatus(consultation) : false,
      },
      actionStatus: {
        isCompleted: lastFeedbackAction ? isCallActionCompleted(lastFeedbackAction?.type) : false,
        isCancelled: lastFeedbackAction ? isCallActionCancelled(lastFeedbackAction?.type) : false,
      },
      hasVideoCall: !!consultation?.videoCallId,
      missedCalls: consultation ? getMissedCalls(consultation) : 0,
      type: consultation?.type,
      startTime: consultation?.startTime,
      endTime: consultation?.endTime,
      clinician: consultation?.clinician,
      createdAt: consultation?.createdAt,
    }
  }, [order])

  return { patientConsultation, error, isLoading: !error && !order, refresh: () => mutate() }
}

export const NO_REFRESH = 0
